import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Send, Sparkles } from "lucide-react";

import { usePrompts, useActivePromptId } from "@/lib/prompts-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";

interface PlaygroundProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PromptPlayground({ open, onOpenChange }: PlaygroundProps) {
  const activeId = useActivePromptId();
  const prompts = usePrompts();
  const activePrompt = useMemo(() => prompts.find((prompt) => prompt.id === activeId) ?? null, [prompts, activeId]);

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState("");
  const [variables, setVariables] = useState<Record<string, string>>({});

  const extractedVars = useMemo(() => {
    if (!activePrompt) return [];
    const matches = activePrompt.content.match(/\{\{([a-zA-Z0-9_]+)\}\}/g) || [];
    return [...new Set(matches.map((value) => value.slice(2, -2)))];
  }, [activePrompt]);

  useEffect(() => {
    if (activePrompt) {
      const initialVars = extractedVars.reduce<Record<string, string>>((acc, key) => ({ ...acc, [key]: "" }), {});
      setVariables(initialVars);
      setResponse("");
      setIsLoading(false);
    }
  }, [activePrompt, open, extractedVars]);

  const handleVariableChange = (key: string, value: string) => {
    setVariables((prev) => ({ ...prev, [key]: value }));
  };

  const handleRunTest = () => {
    if (!activePrompt) return;

    setIsLoading(true);
    setResponse("");

    let filledPrompt = activePrompt.content;
    for (const key in variables) {
      filledPrompt = filledPrompt.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), variables[key] || `[${key}]`);
    }

    const previewResponse = [
      "Bản xem trước cục bộ",
      "",
      `Model: ${activePrompt.model}`,
      `Temperature: ${activePrompt.temperature}`,
      "",
      "Kết quả này được dựng cục bộ để xác minh biến đầu vào và cấu trúc prompt.",
      "",
      filledPrompt,
    ].join("\n");

    setResponse(previewResponse);
    setIsLoading(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col border-white/10 p-0 shadow-2xl sm:max-w-[60%] lg:max-w-[50%]">
        <SheetHeader className="border-b border-white/5 bg-muted/20 p-6">
          <SheetTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Prompt Playground
          </SheetTitle>
          <SheetDescription>Xem trước prompt &quot;{activePrompt?.name}&quot; theo dữ liệu đầu vào hiện tại.</SheetDescription>
        </SheetHeader>

        <div className="grid min-h-0 flex-1 grid-cols-2">
          <div className="flex flex-col border-r border-white/5">
            <h3 className="border-b border-white/5 p-4 text-sm font-semibold">Đầu vào</h3>
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                <h4 className="text-xs font-medium uppercase text-muted-foreground">Biến khả dụng</h4>
                {extractedVars.length > 0 ? (
                  extractedVars.map((variable) => (
                    <div key={variable} className="space-y-1.5">
                      <Label htmlFor={variable} className="text-xs">
                        {variable}
                      </Label>
                      <Input
                        id={variable}
                        value={variables[variable] || ""}
                        onChange={(event) => handleVariableChange(variable, event.target.value)}
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">Prompt này chưa khai báo biến đầu vào.</p>
                )}
              </div>
            </ScrollArea>

            <div className="border-t border-white/5 p-4">
              <Button className="w-full gap-2" onClick={handleRunTest} disabled={isLoading}>
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {isLoading ? "Đang dựng bản xem trước..." : "Chạy xem trước"}
              </Button>
            </div>
          </div>

          <div className="flex flex-col">
            <h3 className="border-b border-white/5 p-4 text-sm font-semibold">Đầu ra</h3>
            <ScrollArea className="flex-1 p-4">
              {isLoading ? <p className="animate-pulse text-sm text-muted-foreground">Đang dựng bản xem trước...</p> : null}

              {response ? (
                <div className="space-y-4">
                  <div className="whitespace-pre-wrap rounded-lg bg-muted/50 p-4 font-mono text-xs leading-relaxed">
                    {response}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div>
                      <p className="text-muted-foreground">Tokens</p>
                      <p className="font-bold">~{Math.floor(response.length / 4)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Latency</p>
                      <p className="font-bold">Instant</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Cost</p>
                      <p className="font-bold">$0.0000</p>
                    </div>
                  </div>
                </div>
              ) : null}
            </ScrollArea>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
